import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import PrintHeader from '../Common/PrintHeader';
import './Forms.css';

const ResidentialUnitReceiptForm = () => {
  const { t } = useTranslation();
  const printRef = useRef(null);
  const [formData, setFormData] = useState({
    logo: null,
    companyName: '',
    companyNameEng: '',
    contractNumber: '',
    startDate: '',
    propertyName: '',
    endDate: '',
    unitNumber: '',
    lessorName: '',
    lessorId: '',
    lessorIdType: 'national',
    lessorRole: 'owner',
    lessorReceiptStatus: '',
    lessorNotes: '',
    tenantName: '',
    tenantId: '',
    tenantIdType: 'national',
    tenantRole: 'tenant',
    tenantReceiptStatus: '',
    clause1: '',
    clause2: '',
    clause3: '',
    clause4: '',
    clause5: '',
    clause6: '',
    clause7: '',
    lessorSignature: '',
    tenantSignature: '',
  });

  const fillSampleData = () => {
    setFormData({
      logo: null,
      companyName: 'العالمية للخدمات العقارية',
      companyNameEng: 'Al-Salmi Real Estate Services',
      contractNumber: '0022555888',
      startDate: '2020-10-01',
      propertyName: 'الشهيل',
      endDate: '2025-10-28',
      unitNumber: '5',
      lessorName: 'يحي دخيل الله يحي ال الشهيل',
      lessorId: '10002548755',
      lessorIdType: 'resident',
      lessorRole: 'وسيط عن المالك',
      lessorReceiptStatus: 'نعم / تم التسليم',
      lessorNotes: 'الوحدة في حالة جيدة',
      tenantName: 'علي بن صالح حسين ال مسعود اليامي',
      tenantId: '1002230835',
      tenantIdType: 'national',
      tenantRole: 'مستأجر',
      tenantReceiptStatus: 'نعم / تم الاستلام',
      clause1: 'تم استلام رقم حساب الكهرباء للعقار المؤجر، مدفوع بالكامل، والتعهد بدفع فواتير الكهرباء المستقبلية خلال مدة الإيجار.',
      clause2: 'يمنع المستأجر من إجراء أي تعديلات على الواجهات الخارجية أو الترتيبات الداخلية للعقار المؤجر دون موافقة خطية من المالك أو من يمثله.',
      clause3: 'يمنع المستأجر من وضع مواد خطرة أو بضائع تجارية أو إزعاج السكان الآخرين أو وضع أثاث في ممرات المبنى.',
      clause4: 'تعهد المستأجر بإبلاغ المالك أو ممثله فوراً عن أي عيوب طبيعية أو أعطال أو أخطاء في العقار.',
      clause5: 'تعهد المستأجر بتحمل جميع مصاريف إصلاح الأضرار الناجمة عن أفعاله المتعمدة أو إهماله أو سوء استخدامه.',
      clause6: 'يمنع المستأجر من إخلاء العقار أو المطالبة بالضمان قبل تسوية جميع الفواتير أو الأضرار.',
      clause7: 'في حالة الاختلاف أو عدم الامتثال لشروط العقد، يتحمل المستأجر جميع التكاليف المالية مع الجهات الرسمية، بما في ذلك المحاكم.',
      lessorSignature: 'يحي دخيل الله',
      tenantSignature: 'علي بن صالح',
    });
  };

  const handleChange = async (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      const file = files[0];
      setFormData({ ...formData, [name]: file });
      
      // File is stored in formData, no need for separate logoDataUrl
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handlePrint = async () => {
    try {
      const element = printRef.current;
      
      // Wait for images to load
      const images = element.querySelectorAll('img');
      const imagePromises = Array.from(images).map((img) => {
        if (img.complete && img.naturalHeight !== 0) {
          return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => resolve(); // Resolve even on error to continue
          // Timeout after 5 seconds
          setTimeout(() => resolve(), 5000);
        });
      });
      
      await Promise.all(imagePromises);

      // Small delay to ensure rendering is complete
      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: '#ffffff',
        imageTimeout: 15000,
        removeContainer: false,
      });
      
      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf = new jsPDF('portrait', 'mm', 'a4');
      const pageWidth = 210;
      const imgWidth = pageWidth * 0.7; // 30% smaller
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const marginX = (pageWidth - imgWidth) / 2;
      let heightLeft = imgHeight;

      let position = 0;
      pdf.addImage(imgData, 'PNG', marginX, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', marginX, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error('Print error:', error);
      alert('Failed to generate PDF. Please try again. Error: ' + error.message);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title">
          <i className="fas fa-home page-icon"></i>
          <h1>{t('forms.residentialUnitReceipt.title', 'Residential Unit Receipt Form')}</h1>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={fillSampleData}
          style={{ backgroundColor: '#28a745' }}
        >
          <i className="fas fa-fill"></i> {t('forms.fillSampleData', 'Fill Sample Data')}
        </button>
      </div>

      <div className="form-container">
        <form>
          {/* Company Information */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-image"></i> {t('forms.logo', 'Logo')}
              </label>
              <div className="input-wrapper">
                <input
                  type="file"
                  className="form-input"
                  name="logo"
                  accept="image/*"
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-building"></i> {t('forms.companyName', 'Company Name (Arabic)')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-building input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-building"></i> {t('forms.companyNameEng', 'Company Name (English)')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-building input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="companyNameEng"
                  value={formData.companyNameEng}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              {/* Empty placeholder to maintain 4-column layout */}
            </div>
          </div>

          {/* Unit Information */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-file-contract"></i> {t('forms.contractNumber', 'Contract Number')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-file-contract input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="contractNumber"
                  value={formData.contractNumber}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-calendar-check"></i> {t('forms.startDate', 'Start Date')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-calendar-check input-icon"></i>
                <input
                  type="date"
                  className="form-input"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-building"></i> {t('forms.propertyName', 'Property Name')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-building input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="propertyName"
                  value={formData.propertyName}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-calendar-times"></i> {t('forms.endDate', 'End Date')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-calendar-times input-icon"></i>
                <input
                  type="date"
                  className="form-input"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>


          {/* Lessor Information */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-user"></i> {t('forms.lessorName', 'Lessor Name')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-user input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="lessorName"
                  value={formData.lessorName}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-id-card"></i> {t('forms.lessorId', 'Lessor ID Number')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-id-card input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="lessorId"
                  value={formData.lessorId}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-id-badge"></i> {t('forms.lessorIdType', 'ID Type')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-id-badge input-icon"></i>
                <select
                  className="form-input"
                  name="lessorIdType"
                  value={formData.lessorIdType}
                  onChange={handleChange}
                >
                  <option value="national">National ID / هوية وطنية</option>
                  <option value="resident">Resident ID / هوية مقيم</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-user-tag"></i> {t('forms.lessorRole', 'Party Role')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-user-tag input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="lessorRole"
                  value={formData.lessorRole}
                  onChange={handleChange}
                  placeholder="Owner / Agent"
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-hashtag"></i> {t('forms.unitNumber', 'Unit Number')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-hashtag input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="unitNumber"
                  value={formData.unitNumber}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-check-circle"></i> {t('forms.lessorReceiptStatus', 'Lessor Receipt Status')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-check-circle input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="lessorReceiptStatus"
                  value={formData.lessorReceiptStatus}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-sticky-note"></i> {t('forms.lessorNotes', 'Lessor Notes')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-sticky-note input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="lessorNotes"
                  value={formData.lessorNotes}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              {/* Empty placeholder to maintain 4-column layout */}
            </div>
          </div>

          {/* Tenant Information */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-user"></i> {t('forms.tenantName', 'Tenant Name')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-user input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="tenantName"
                  value={formData.tenantName}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-id-card"></i> {t('forms.tenantId', 'Tenant ID Number')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-id-card input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="tenantId"
                  value={formData.tenantId}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-id-badge"></i> {t('forms.tenantIdType', 'ID Type')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-id-badge input-icon"></i>
                <select
                  className="form-input"
                  name="tenantIdType"
                  value={formData.tenantIdType}
                  onChange={handleChange}
                >
                  <option value="national">National ID / هوية وطنية</option>
                  <option value="resident">Resident ID / هوية مقيم</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-check-circle"></i> {t('forms.tenantReceiptStatus', 'Tenant Receipt Status')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-check-circle input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="tenantReceiptStatus"
                  value={formData.tenantReceiptStatus}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Clauses */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-paragraph"></i> {t('forms.clause1', 'Clause 1')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-paragraph input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="clause1"
                  value={formData.clause1}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-paragraph"></i> {t('forms.clause2', 'Clause 2')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-paragraph input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="clause2"
                  value={formData.clause2}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-paragraph"></i> {t('forms.clause3', 'Clause 3')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-paragraph input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="clause3"
                  value={formData.clause3}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-paragraph"></i> {t('forms.clause4', 'Clause 4')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-paragraph input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="clause4"
                  value={formData.clause4}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-paragraph"></i> {t('forms.clause5', 'Clause 5')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-paragraph input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="clause5"
                  value={formData.clause5}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-paragraph"></i> {t('forms.clause6', 'Clause 6')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-paragraph input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="clause6"
                  value={formData.clause6}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-paragraph"></i> {t('forms.clause7', 'Clause 7')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-paragraph input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="clause7"
                  value={formData.clause7}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              {/* Empty placeholder to maintain 4-column layout */}
            </div>
          </div>

          {/* Signatures */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-signature"></i> {t('forms.lessorSignature', 'Lessor Signature')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-signature input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="lessorSignature"
                  value={formData.lessorSignature}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-signature"></i> {t('forms.tenantSignature', 'Tenant Signature')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-signature input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="tenantSignature"
                  value={formData.tenantSignature}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              {/* Empty placeholder to maintain 4-column layout */}
            </div>
            <div className="form-group">
              {/* Empty placeholder to maintain 4-column layout */}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handlePrint}
            >
              <i className="fas fa-print"></i> {t('forms.print', 'Print to PDF')}
            </button>
          </div>
        </form>
      </div>

      {/* Print Preview */}
      <div ref={printRef} className="print-container" style={{ display: 'none' }}>
        <div className="unit-receipt-print">
          {/* Print Header with Organization Details */}
          <div style={{ marginBottom: '20px' }}>
            <PrintHeader 
              title={t('forms.residentialUnitReceipt', 'Residential Unit Receipt Form')}
              subtitle="نموذج إستلام وحدة سكنية"
              showDate={true}
            />
          </div>
          <div className="unit-info">
            <div className="info-row">
              <span className="label">رقم العقد / Contract Number:</span>
              <span className="value">{formData.contractNumber || '______________'}</span>
            </div>
            <div className="info-row">
              <span className="label">تاريخ البدء / Start Date:</span>
              <span className="value">{formData.startDate || '______________'}</span>
            </div>
            <div className="info-row">
              <span className="label">اسم العقار / Property Name:</span>
              <span className="value">{formData.propertyName || '______________'}</span>
            </div>
            <div className="info-row">
              <span className="label">تاريخ الانتهاء / End Date:</span>
              <span className="value">{formData.endDate || '______________'}</span>
            </div>
            <div className="info-row">
              <span className="label">رقم الوحدة / Unit Number:</span>
              <span className="value">{formData.unitNumber || '______________'}</span>
            </div>
          </div>
          <div className="party-section">
            <h3>الطرف الأول (المؤجر) / First Party (Lessor)</h3>
            <div className="info-row">
              <span className="label">الأسم / Name:</span>
              <span className="value">{formData.lessorName || '______________'}</span>
            </div>
            <div className="info-row">
              <span className="label">رقم الهوية / ID Number:</span>
              <span className="value">{formData.lessorId || '______________'}</span>
            </div>
            <div className="info-row">
              <span className="label">نوع الهوية / ID Type:</span>
              <span className="value">{formData.lessorIdType === 'national' ? 'هوية وطنية / National ID' : 'هوية مقيم / Resident ID'}</span>
            </div>
            <div className="info-row">
              <span className="label">صفة الطرف / Party Role:</span>
              <span className="value">{formData.lessorRole || '______________'}</span>
            </div>
            <div className="info-row">
              <span className="label">حالة استلام / تسليم الوحدة:</span>
              <span className="value">{formData.lessorReceiptStatus || '______________'}</span>
            </div>
            <div className="info-row">
              <span className="label">الملاحظات على حالة الوحدة:</span>
              <span className="value">{formData.lessorNotes || '______________'}</span>
            </div>
          </div>
          <div className="party-section">
            <h3>الطرف الثاني (المستأجر) / Second Party (Tenant)</h3>
            <div className="info-row">
              <span className="label">الأسم / Name:</span>
              <span className="value">{formData.tenantName || '______________'}</span>
            </div>
            <div className="info-row">
              <span className="label">رقم الهوية / ID Number:</span>
              <span className="value">{formData.tenantId || '______________'}</span>
            </div>
            <div className="info-row">
              <span className="label">نوع الهوية / ID Type:</span>
              <span className="value">{formData.tenantIdType === 'national' ? 'هوية وطنية / National ID' : 'هوية مقيم / Resident ID'}</span>
            </div>
            <div className="info-row">
              <span className="label">صفة الطرف / Party Role:</span>
              <span className="value">مستأجر / Tenant</span>
            </div>
            <div className="info-row">
              <span className="label">حالة استلام / تسليم الوحدة:</span>
              <span className="value">{formData.tenantReceiptStatus || '______________'}</span>
            </div>
          </div>
          <div className="clauses-section">
            <h3>بنود الأستلام / التسليم / Terms of Receipt / Delivery</h3>
            {formData.clause1 && <p><strong>البند الأول:</strong> {formData.clause1}</p>}
            {formData.clause2 && <p><strong>البند الثاني:</strong> {formData.clause2}</p>}
            {formData.clause3 && <p><strong>البند الثالث:</strong> {formData.clause3}</p>}
            {formData.clause4 && <p><strong>البند الرابع:</strong> {formData.clause4}</p>}
            {formData.clause5 && <p><strong>البند الخامس:</strong> {formData.clause5}</p>}
            {formData.clause6 && <p><strong>البند السادس:</strong> {formData.clause6}</p>}
            {formData.clause7 && <p><strong>البند السابع:</strong> {formData.clause7}</p>}
          </div>
          <div className="signatures-section">
            <div className="signature-box">
              <p>توقيع الطرف الأول (المؤجر) / Signature of First Party (Lessor)</p>
              <p>{formData.lessorSignature || '______________'}</p>
            </div>
            <div className="signature-box">
              <p>توقيع الطرف الثاني (المستأجر) / Signature of Second Party (Tenant)</p>
              <p>{formData.tenantSignature || '______________'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResidentialUnitReceiptForm;

